import React, { useState } from 'react';
import { API_CONFIG } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';

interface AdminTestResult {
  isAdmin: boolean;
  email?: string;
  userId?: string;
  error?: string;
  responseTime?: number;
}

const AdminPermissionTest: React.FC = () => {
  const { user } = useAuth();
  const [testResult, setTestResult] = useState<AdminTestResult | null>(null);
  const [loading, setLoading] = useState(false);

  const testAdminPermission = async () => {
    if (!user) {
      setTestResult({ isAdmin: false, error: '用户未登录' });
      return;
    }

    setLoading(true);
    const startTime = Date.now();

    try {
      // 获取当前用户的 session token
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.access_token) {
        setTestResult({ 
          isAdmin: false, 
          error: '获取 session 失败',
          responseTime: Date.now() - startTime
        });
        return;
      }

      // 调用后端 Edge Function 检查管理员权限
      const functionsUrl = API_CONFIG.FUNCTIONS_URL || `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
      const response = await fetch(`${functionsUrl}/check-admin`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
      });

      const responseTime = Date.now() - startTime;

      if (!response.ok) {
        setTestResult({
          isAdmin: false,
          error: `请求失败: ${response.status} ${response.statusText}`,
          responseTime
        });
        return;
      }

      const result = await response.json();
      setTestResult({
        ...result,
        responseTime
      });

    } catch (error) {
      setTestResult({
        isAdmin: false,
        error: `网络错误: ${error instanceof Error ? error.message : '未知错误'}`,
        responseTime: Date.now() - startTime
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg max-w-2xl mx-auto">
      <h3 className="text-xl font-bold mb-4 text-gray-800">管理员权限测试</h3>
      
      <div className="mb-4">
        <p className="text-sm text-gray-600 mb-2">
          <strong>当前用户:</strong> {user?.email || '未登录'}
        </p>
        <p className="text-sm text-gray-600 mb-4">
          <strong>用户ID:</strong> {user?.id || '无'}
        </p>
      </div>

      <button 
        onClick={testAdminPermission}
        disabled={loading || !user}
        className="bg-blue-500 hover:bg-blue-700 disabled:bg-gray-400 text-white font-bold py-2 px-4 rounded mb-4"
      >
        {loading ? '测试中...' : '测试管理员权限'}
      </button>

      {testResult && (
        <div className="border rounded-lg p-4">
          <h4 className="font-semibold mb-2">测试结果:</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm">
                <strong>管理员状态:</strong> 
                <span className={`ml-2 px-2 py-1 rounded text-xs ${
                  testResult.isAdmin 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {testResult.isAdmin ? '✅ 是管理员' : '❌ 非管理员'}
                </span>
              </p>
              
              {testResult.email && (
                <p className="text-sm mt-2">
                  <strong>邮箱:</strong> {testResult.email}
                </p>
              )}
              
              {testResult.userId && (
                <p className="text-sm mt-2">
                  <strong>用户ID:</strong> {testResult.userId}
                </p>
              )}
            </div>
            
            <div>
              {testResult.responseTime && (
                <p className="text-sm">
                  <strong>响应时间:</strong> {testResult.responseTime}ms
                </p>
              )}
              
              {testResult.error && (
                <p className="text-sm mt-2 text-red-600">
                  <strong>错误:</strong> {testResult.error}
                </p>
              )}
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-gray-50 rounded">
            <h5 className="font-semibold text-sm mb-2">完整响应:</h5>
            <pre className="text-xs bg-white p-2 rounded border overflow-auto">
              {JSON.stringify(testResult, null, 2)}
            </pre>
          </div>
        </div>
      )}
      
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-800 mb-2">说明:</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• 此测试直接调用后端 Edge Function 检查管理员权限</li>
          <li>• 只有邮箱为 yebaobao888@gmail.com 的用户才会被认定为管理员</li>
          <li>• 权限判断完全在后端进行，提高了安全性</li>
          <li>• 响应时间显示了 API 调用的性能</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminPermissionTest;